import { queryOptions } from "@tanstack/react-query";
import type { Company } from "@types";
import { createAuthenticatedClient } from "./apiClient";
import { shouldRetryCompanyBySlug } from "./companyBySlugError";
import { queryKeys } from "./queryKeys";

/**
 * Shared query options for resolving a company from its URL slug via
 * `GET /c/{slug}` (SD-11894). Used by the `/:slug` router loader
 * (`companySlugLoader`) so a cold-load resolution
 * in the loader and any later component read hit the SAME cache entry.
 *
 * Keyed account-scoped: a slug is only unique per account, so `accountId` is
 * part of the identity to avoid a cross-account cache collision or leak. The
 * caller must have a `slug` + `accessToken` + `accountId` before requesting
 * these options (the loader/hook gate on that).
 *
 * Routed through `createAuthenticatedClient` so it inherits the 401 silent-
 * reauth retry and the auth-origin guard. 404/403 (and other 4xx) are stable
 * for this slug + caller — `shouldRetryCompanyBySlug` surfaces them immediately
 * and retries only transient network/5xx failures.
 */
export const companyBySlugQueryOptions = (
  slug: string,
  accessToken: string,
  accountId: string,
) =>
  queryOptions<Company>({
    queryKey: queryKeys.companyBySlug(accountId, slug),
    queryFn: async () => {
      // Runtime guard: `enabled` gates the hook, but TanStack Query still runs
      // `queryFn` on a manual `refetch()` (and some invalidate-driven refetches)
      // even when disabled. Without this, an options object built with the
      // empty-string fallbacks would send `Authorization: Bearer ` or hit `/c/`.
      if (!slug || !accessToken) {
        throw new Error(
          "slug and accessToken are required to resolve a company",
        );
      }
      const client = createAuthenticatedClient(accessToken);
      const response = await client.get<Company>(
        `/c/${encodeURIComponent(slug)}`,
      );
      return response.data;
    },
    retry: shouldRetryCompanyBySlug,
  });
